import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getRef } from "../lib/referral";
import axios from "axios";
import { toast } from "sonner";
import { CalendarDays, MessageCircle, Phone, ArrowUpRight, Check } from "lucide-react";
import { Reveal, MaskLines } from "./Reveal";
import { useLang, SERVICES, VCALENDAR, WHATSAPP, PHONE, IMG } from "../lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", phone: "", email: "", service: "", preferred_date: "", preferred_time: "", notes: "", referral_code: "" };

const Field = ({ label, children }) => (
  <label className="block">
    <span className="overline text-[#5a534c] text-[0.62rem]">{label}</span>
    {children}
  </label>
);

const Direct = ({ href, icon: Icon, title, sub, id }) => (
  <a data-testid={id} href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noreferrer"
    className="group flex items-center gap-5 py-6 border-b border-[#F6F2EC]/15 transition-[padding] duration-500 hover:pl-3">
    <span className="w-12 h-12 rounded-full border border-[#C9A66B]/60 flex items-center justify-center text-[#C9A66B] transition-colors duration-500 group-hover:bg-[#C9A66B] group-hover:text-[#14110F]"><Icon className="w-5 h-5" strokeWidth={1.3} /></span>
    <span className="flex-1">
      <span className="block font-serif-d text-2xl sm:text-3xl font-light">{title}</span>
      <span className="block text-sm text-[#b8ad9f]">{sub}</span>
    </span>
    <ArrowUpRight className="w-5 h-5 text-[#C9A66B] transition-transform duration-500 group-hover:rotate-45" />
  </a>
);

export const Booking = ({ picked }) => {
  const { t, lang } = useLang();
  const b = t.booking;
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (picked) setForm((f) => ({ ...f, service: picked }));
  }, [picked]);

  useEffect(() => {
    const apply = () => { const r = getRef(); if (r?.code) setForm((f) => (f.referral_code ? f : { ...f, referral_code: r.code })); };
    apply();
    window.addEventListener("gioia-ref", apply);
    return () => window.removeEventListener("gioia-ref", apply);
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const all = SERVICES.flatMap((s) => s[lang].s);
  const options = form.service && !all.includes(form.service) ? [form.service, ...all] : all;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form, lang, email: form.email || null, referral_code: form.referral_code.trim() || null };
      const res = await axios.post(`${API}/appointments`, payload);
      setDone(true);
      setForm(EMPTY);
      navigate("/grazie", { state: { name: form.name, service: form.service, date: form.preferred_date, time: form.preferred_time, referrer: res.data?.referrer_name ?? null } });
    } catch {
      toast.error(b.err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="booking" data-testid="booking-section" className="bg-[#ECE5DA]">
      <div className="max-w-[1440px] mx-auto grid lg:grid-cols-12">
        <div className="lg:col-span-7 px-5 sm:px-10 py-24 sm:py-32">
          <Reveal><p className="overline text-[#8A6A3A] mb-6">{b.over}</p></Reveal>
          <h2 className="font-serif-d font-light text-[2.8rem] sm:text-7xl leading-[0.95] text-[#14110F]">
            <MaskLines key={b.title[0]} inView lines={[b.title[0]]} />
            <MaskLines key={b.title[1]} inView delay={0.15} lines={[b.title[1]]} lineClass="italic text-[#8A6A3A]" />
          </h2>
          <Reveal delay={0.1}><p className="mt-8 text-[#4f4841] font-light max-w-lg leading-relaxed">{b.sub}</p></Reveal>

          <Reveal delay={0.15}>
            <form onSubmit={submit} data-testid="booking-form" className="mt-14 grid sm:grid-cols-2 gap-x-10 gap-y-8">
              <Field label={b.name}><input required minLength={2} data-testid="booking-name-input" className="input-line" value={form.name} onChange={set("name")} /></Field>
              <Field label={b.phone}><input required type="tel" minLength={5} data-testid="booking-phone-input" className="input-line" value={form.phone} onChange={set("phone")} /></Field>
              <Field label={b.email}><input type="email" data-testid="booking-email-input" className="input-line" value={form.email} onChange={set("email")} /></Field>
              <Field label={b.service}>
                <select required data-testid="booking-service-select" className="input-line cursor-pointer" value={form.service} onChange={set("service")}>
                  <option value="" disabled>{b.choose}</option>
                  {options.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label={b.date}><input type="date" data-testid="booking-date-input" className="input-line" value={form.preferred_date} onChange={set("preferred_date")} min={new Date().toISOString().slice(0, 10)} /></Field>
              <Field label={b.time}>
                <select data-testid="booking-time-select" className="input-line cursor-pointer" value={form.preferred_time} onChange={set("preferred_time")}>
                  <option value="">—</option>
                  {b.times.map((x) => <option key={x} value={x}>{x}</option>)}
                </select>
              </Field>
              <div className="sm:col-span-2"><Field label={b.notes}><textarea rows={2} data-testid="booking-notes-input" className="input-line resize-none" value={form.notes} onChange={set("notes")} /></Field></div>
              <Field label={b.refCode}><input data-testid="booking-referral-input" className="input-line uppercase" value={form.referral_code} onChange={set("referral_code")} placeholder="Es. MARIA-3F2A" /></Field>
              <label className="sm:col-span-2 flex items-start gap-3 text-sm text-[#4f4841] cursor-pointer">
                <input type="checkbox" required data-testid="booking-consent-checkbox" className="mt-1 accent-[#8A6A3A] w-4 h-4" />
                <span>{b.consent} <Link to="/privacy" data-testid="booking-privacy-link" className="underline underline-offset-4 decoration-[#8A6A3A] hover:text-[#8A6A3A]">{b.privacy}</Link></span>
              </label>
              <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center gap-6">
                <button type="submit" disabled={loading} data-testid="booking-submit-btn"
                  className="group inline-flex items-center justify-center gap-4 rounded-full bg-[#14110F] text-[#F6F2EC] px-9 py-4 text-[0.75rem] tracking-[0.2em] uppercase transition-colors duration-500 hover:bg-[#8A6A3A] disabled:opacity-60">
                  {loading ? b.sending : b.send}
                  <ArrowUpRight className="w-4 h-4 transition-transform duration-500 group-hover:rotate-45" />
                </button>
                {done && <p data-testid="booking-success-msg" className="flex items-center gap-2 text-sm text-[#586048]"><Check className="w-4 h-4" /> {b.ok}</p>}
              </div>
            </form>
          </Reveal>
        </div>
        <div className="lg:col-span-5 bg-[#14110F] text-[#F6F2EC] px-5 sm:px-10 lg:px-14 py-20 sm:py-32 flex flex-col justify-center grain relative overflow-hidden">
          <img src={IMG.realMassage} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover object-[55%_40%] opacity-50" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#14110F]/40 via-[#14110F]/70 to-[#14110F]/90" />
          <Reveal className="relative">
            <p className="overline text-[#C9A66B] mb-6">{b.direct}</p>
            <div className="border-t border-[#F6F2EC]/15">
              <Direct id="booking-vcalendar-link" href={VCALENDAR} icon={CalendarDays} title={b.vcal} sub={b.vcalSub} />
              <Direct id="booking-whatsapp-link" href={WHATSAPP} icon={MessageCircle} title={b.wa} sub={b.waSub} />
              <Direct id="booking-call-link" href={PHONE} icon={Phone} title={b.call} sub={b.callSub} />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
